import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { NOTE_MAX } from '../../../../../Consts/billing-documents.const';
import {
  REFUND_REASON_LABEL as REASON_LABELS,
  REFUND_REASON_OPTIONS,
} from '../../../../../Enums/money-out.enum';
import {
  RefundDialogData,
  RefundDialogResult,
} from '../../../Interfaces/billing-dialog.interface';

@Component({
  selector: 'app-refund-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, MatDialogModule, MatIconModule, TranslateModule],
  templateUrl: './refund-dialog.component.html',
  styleUrl: './refund-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RefundDialogComponent {
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _dialogRef =
    inject<MatDialogRef<RefundDialogComponent, RefundDialogResult>>(MatDialogRef);

  readonly data = inject<RefundDialogData>(MAT_DIALOG_DATA);

  readonly noteMax = NOTE_MAX;
  readonly reasonOptions = REFUND_REASON_OPTIONS;
  readonly reasonLabels = REASON_LABELS;

  readonly amount = signal<number | null>(null);

  readonly isAmountValid = computed<boolean>(() => {
    const value = this.amount();

    return value === null || (value > 0 && value <= this.data.refundableRemainder);
  });

  readonly formGroup: FormGroup = this._formBuilder.group({
    amount: [null],
    reason: [this.reasonOptions[0], [Validators.required]],
    note: ['', [Validators.maxLength(NOTE_MAX)]],
  });

  constructor() {
    this._registerAmountSync();
  }

  confirm(): void {
    if (!this.formGroup.valid || !this.isAmountValid()) {
      return;
    }

    this._dialogRef.close({
      amount: this.amount(),
      reason: String(this.formGroup.value.reason),
      note: String(this.formGroup.value.note ?? '').trim() || null,
    });
  }

  dismiss(): void {
    this._dialogRef.close();
  }

  private _registerAmountSync(): void {
    this.formGroup.controls['amount'].valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this._applyAmount(value));
  }

  private _applyAmount(value: unknown): void {
    if (value === null || value === '' || value === undefined) {
      this.amount.set(null);

      return;
    }

    const parsed = Number(value);

    this.amount.set(Number.isFinite(parsed) ? parsed : 0);
  }
}
